import React, { useState, useEffect, useCallback } from 'react';
import nextlaneApi from '../services/nextlane';
import './NextlaneOR.css';

const RO_STATUTS = {
  'RO created':             { label: 'Créé',     color: '#f97316', bg: 'light-dark(#fff7ed, var(--color-paused-light))' },
  'RO printed':             { label: 'Imprimé',  color: 'light-dark(#3b82f6, var(--color-info-dark))', bg: 'light-dark(#eff6ff, var(--color-info-light))' },
  'Final invoice issued':   { label: 'Facturé',  color: 'light-dark(#22c55e, var(--color-success-dark))', bg: 'light-dark(#f0fdf4, var(--bg-muted))' },
  'RO cancelled':           { label: 'Annulé',   color: '#94a3b8', bg: 'light-dark(#f8fafc, var(--bg-muted))' },
};
const getStatut = (s) => RO_STATUTS[s] || { label: s || '—', color: 'var(--text-muted)', bg: 'var(--bg-muted)' };

const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const formatMoney = (n) => n != null ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '—';
const formatKm = (n) => n ? Number(n).toLocaleString('fr-FR') + ' km' : '—';

// Dealerships Datacar avec OR actifs, rattachés à leur concession CRM (même
// correspondance que backend/src/routes/nextlane.js, par nom de concession).
const DEALERSHIPS = [
  { id: '02E48C35-E7D5-4364-BFF0-339C17F393BF', name: 'SOGUAVA', concession: 'SOGUAVASOREC' },
  { id: '431AEE51-064A-4DCE-B43C-781B0AE992AB', name: 'CENTRE AUTO', concession: 'CENTRAUTODIS' },
];

function NextlaneOR({ user, setHeaderAction }) {
  const [ors, setOrs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  // Concession : suit le choix du menu (2026-10-26) — la page est remontée à chaque changement.
  // Menu sur une concession sans DMS Datacar (BLUE, PBS…) : aucun OR, jamais ceux des autres.
  const [filtreDealership] = useState(() => (
    user?.concession_nom ? (DEALERSHIPS.find(d => d.concession === user.concession_nom)?.id || null) : ''
  ));
  const sansDms = filtreDealership === null;
  const [filtreDept, setFiltreDept] = useState('');
  const [filtreFinished, setFiltreFinished] = useState('false'); // par défaut : en cours
  const [page, setPage] = useState(1);
  const [selectedOR, setSelectedOR] = useState(null);

  const loadData = useCallback(async () => {
    if (sansDms) { setOrs([]); setTotal(0); setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (filtreStatut) params.statut = filtreStatut;
      if (filtreDealership) params.dealership = filtreDealership;
      if (filtreDept) params.department = filtreDept;
      if (filtreFinished) params.finished = filtreFinished;
      const { data } = await nextlaneApi.getORList(params);
      setOrs(data.ors || []);
      setTotal(data.total || 0);
    } catch { setOrs([]); }
    finally { setLoading(false); }
  }, [search, filtreStatut, filtreDealership, sansDms, filtreDept, filtreFinished, page]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { nextlaneApi.getORStats().then(r => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { nextlaneApi.getORDepartments().then(r => setDepartments(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (setHeaderAction) setHeaderAction(null);
  }, [setHeaderAction]);

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="page-container">
      {/* Stats chips */}
      {stats && (
        <div className="nlor-stats-bar">
          <div className="nlor-stat-chip"><strong>{stats.en_cours}</strong><span>OR en cours</span></div>
          <div className="nlor-stat-chip"><strong>{formatMoney(stats.montant_ht_en_cours)}</strong><span>HT en cours</span></div>
          <div className="nlor-stat-chip"><strong>{stats.total}</strong><span>total sync</span></div>
          {stats.derniere_sync && <div className="nlor-stat-chip"><span>Sync : {formatDateTime(stats.derniere_sync)}</span></div>}
          {stats.parDealership?.filter(d => !filtreDealership || d.dealership_name === DEALERSHIPS.find(x => x.id === filtreDealership)?.name).map(d => (
            <div key={d.dealership_name} className="nlor-stat-chip"><strong>{d.en_cours}</strong><span>{d.dealership_name}</span></div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher immat, client, N° OR..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <select className={`page-select${filtreStatut ? ' active-filter' : ''}`} value={filtreStatut} onChange={e => { setFiltreStatut(e.target.value); setPage(1); }}>
          <option value="">Tous statuts</option>
          {Object.entries(RO_STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className={`page-select${filtreDept ? ' active-filter' : ''}`} value={filtreDept} onChange={e => { setFiltreDept(e.target.value); setPage(1); }}>
          <option value="">Tous départements</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className={`page-select${filtreFinished !== 'false' ? ' active-filter' : ''}`} value={filtreFinished} onChange={e => { setFiltreFinished(e.target.value); setPage(1); }}>
          <option value="false">En cours</option>
          <option value="true">Terminés</option>
          <option value="">Tous</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>{total} OR</span>
      </div>

      {/* Tableau */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Statut</th>
              <th>N° OR</th>
              <th>Date</th>
              <th>Concession</th>
              <th>Client</th>
              <th>Immat</th>
              <th>Marque / Modèle</th>
              <th>Km</th>
              <th style={{ textAlign: 'right' }}>Montant HT</th>
              <th>Dept</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="10" style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Chargement...</td></tr>}
            {!loading && ors.length === 0 && <tr><td colSpan="10" style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>{sansDms ? `Aucun OR Datacar synchronisé pour ${user.concession_nom}` : 'Aucun OR'}</td></tr>}
            {!loading && ors.map(or => {
              const st = getStatut(or.ro_status);
              return (
                <tr key={or.id} className="nlor-row" onClick={() => setSelectedOR(or)}>
                  <td><span className="nlor-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span></td>
                  <td style={{ fontWeight: 600, color: 'light-dark(var(--color-accent), var(--color-accent-dark))' }}>{or.folder_number}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.83rem' }}>{formatDate(or.folder_creation_date)}</td>
                  <td style={{ fontSize: '0.83rem' }}>{or.dealership_name}</td>
                  <td style={{ fontWeight: 600 }}>{[or.client_prenom, or.client_nom].filter(Boolean).join(' ') || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.83rem' }}>{or.immat || '—'}</td>
                  <td>
                    <div style={{ fontSize: '0.85rem' }}>{or.brand}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{or.model}</div>
                  </td>
                  <td style={{ fontSize: '0.83rem' }}>{formatKm(or.vehicle_mileage_in)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'monospace', fontSize: '0.85rem' }}>{or.total_ht > 0 ? formatMoney(or.total_ht) : '—'}</td>
                  <td><span className="nlor-badge" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>{or.department_id}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Précédent</button>
          <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Page {page} / {totalPages}</span>
          <button className="btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant →</button>
        </div>
      )}

      {/* Modale détail */}
      {selectedOR && <ORDetailModal or={selectedOR} onClose={() => setSelectedOR(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODALE DÉTAIL
// ═══════════════════════════════════════════════════════════════════════════════

function ORDetailModal({ or: orSummary, onClose }) {
  const [or, setOr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('documents');
  const [expandedDocs, setExpandedDocs] = useState({});

  useEffect(() => {
    setLoading(true);
    nextlaneApi.getORDetail(orSummary.id).then(r => { setOr(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [orSummary.id]);

  const toggleDoc = (id) => setExpandedDocs(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading || !or) {
    return (
      <div className="modal-overlay">
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  const st = getStatut(or.ro_status);
  const docs = or.documents || [];
  const factures = docs.filter(d => d.document_type === 'facture' && !d.is_cancelled);
  const devis = docs.filter(d => d.document_type === 'devis' && !d.is_cancelled);

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            OR {or.folder_number}
            <span className="nlor-badge" style={{ background: st.bg, color: st.color, marginLeft: 12, fontSize: '0.8rem' }}>{st.label}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Résumé header */}
          <div className="nlor-detail-grid">
            <div className="nlor-detail-section">
              <h4>Client</h4>
              <div className="nlor-field"><span className="nlor-field-label">Nom</span><span className="nlor-field-value" style={{ fontWeight: 600 }}>{[or.client_prenom, or.client_nom].filter(Boolean).join(' ') || '—'}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Téléphone</span><span className="nlor-field-value">{or.client_tel || '—'}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Email</span><span className="nlor-field-value">{or.client_email || '—'}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Adresse</span><span className="nlor-field-value" style={{ fontSize: '0.8rem' }}>{or.client_adresse || '—'}</span></div>
            </div>
            <div className="nlor-detail-section">
              <h4>Véhicule</h4>
              <div className="nlor-field"><span className="nlor-field-label">Immat</span><span className="nlor-field-value" style={{ fontWeight: 600, fontFamily: 'monospace' }}>{or.immat || '—'}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">VIN</span><span className="nlor-field-value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{or.vin || '—'}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Marque / Modèle</span><span className="nlor-field-value">{or.brand} {or.model}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Km entrée</span><span className="nlor-field-value">{formatKm(or.vehicle_mileage_in)}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Carburant</span><span className="nlor-field-value">{or.fuel || '—'}</span></div>
              <div className="nlor-field"><span className="nlor-field-label">Couleur</span><span className="nlor-field-value">{or.colour || '—'}</span></div>
            </div>
          </div>

          {/* Onglets */}
          <div className="nlor-tabs">
            <button className={tab === 'documents' ? 'active' : ''} onClick={() => setTab('documents')}>
              📄 Factures & Devis ({docs.length})
            </button>
            <button className={tab === 'infos' ? 'active' : ''} onClick={() => setTab('infos')}>
              ℹ️ Infos OR
            </button>
          </div>

          {/* Onglet Documents */}
          {tab === 'documents' && (
            <div>
              {docs.length === 0 && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Aucun document</div>}

              {/* Résumé montants */}
              {(factures.length > 0 || devis.length > 0) && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  {factures.length > 0 && (
                    <div style={{ padding: '8px 16px', background: 'var(--color-success-light)', borderRadius: 8, fontSize: '0.85rem' }}>
                      <strong>{factures.length} facture{factures.length > 1 ? 's' : ''}</strong> — {formatMoney(factures.reduce((s, d) => s + d.total_ttc, 0))} TTC
                    </div>
                  )}
                  {devis.length > 0 && (
                    <div style={{ padding: '8px 16px', background: 'var(--color-info-light)', borderRadius: 8, fontSize: '0.85rem' }}>
                      <strong>{devis.length} devis</strong> — {formatMoney(devis.reduce((s, d) => s + d.total_ttc, 0))} TTC
                    </div>
                  )}
                </div>
              )}

              {docs.map(doc => (
                <div key={doc.id} className="nlor-doc-card">
                  <div className="nlor-doc-header" onClick={() => toggleDoc(doc.id)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="nlor-badge" style={{
                        background: doc.document_type === 'facture' ? 'var(--color-success-light)' : doc.document_type === 'devis' ? 'var(--color-info-light)' : 'var(--bg-muted)',
                        color: doc.document_type === 'facture' ? 'var(--color-success)' : doc.document_type === 'devis' ? 'var(--color-info)' : 'var(--text-muted)',
                      }}>{doc.document_type === 'facture' ? 'Facture' : doc.document_type === 'devis' ? 'Devis' : 'Avoir'}</span>
                      <strong>{doc.document_number}</strong>
                      {doc.is_cancelled && <span className="nlor-badge" style={{ background: 'var(--color-error-light)', color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>Annulé</span>}
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{formatDate(doc.fiscal_date)}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Payeur : {doc.payer}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: '0.85rem' }}>
                      <span>HT {formatMoney(doc.total_ht)}</span>
                      <strong>TTC {formatMoney(doc.total_ttc)}</strong>
                      <span style={{ fontSize: '0.75rem' }}>{expandedDocs[doc.id] ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {expandedDocs[doc.id] && (
                    <div className="nlor-doc-body">
                      {(doc.jobs || []).map((job, ji) => (
                        <div key={ji} className="nlor-job-block">
                          <div className="nlor-job-title">
                            {job.advisor && <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>Conseiller : {job.advisor} — </span>}
                            {job.descr || `Job ${job.rank}`}
                            {job.isWarranty && <span className="nlor-badge" style={{ background: 'var(--color-warning-light)', color: 'light-dark(var(--color-warning), var(--color-warning-dark))', marginLeft: 8 }}>Garantie</span>}
                            {job.isCampaign && <span className="nlor-badge" style={{ background: 'var(--color-info-light)', color: 'light-dark(var(--color-info), var(--color-info-dark))', marginLeft: 8 }}>Campagne</span>}
                          </div>
                          {job.complaint && <div className="nlor-job-complaint">{job.complaint}</div>}
                          {(job.items || []).map((it, ii) => (
                            <div key={ii} className="nlor-item-row">
                              <span className={`nlor-item-type ${(it.type || '').toLowerCase()}`}>{it.type || '—'}</span>
                              <span className="nlor-item-descr">{it.descr}</span>
                              {it.ref && <span className="nlor-item-ref">{it.ref}</span>}
                              {it.qty != null && <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', minWidth: 30, textAlign: 'right' }}>×{it.qty}</span>}
                              <span className="nlor-item-price">{it.netPrice != null ? formatMoney(it.netPrice) : ''}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                      {(!doc.jobs || doc.jobs.length === 0) && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>Aucune ligne détaillée</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Onglet Infos OR */}
          {tab === 'infos' && (
            <div className="nlor-detail-grid">
              <div className="nlor-detail-section">
                <h4>Dates</h4>
                <div className="nlor-field"><span className="nlor-field-label">Date RDV</span><span className="nlor-field-value">{formatDateTime(or.appointment_date)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Création OR</span><span className="nlor-field-value">{formatDateTime(or.folder_creation_date)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Début travaux</span><span className="nlor-field-value">{formatDateTime(or.work_begin_date)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Production</span><span className="nlor-field-value">{formatDateTime(or.production_date)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Réception terminée</span><span className="nlor-field-value">{formatDateTime(or.reception_completed_date)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Dernier statut</span><span className="nlor-field-value">{formatDateTime(or.ro_status_date)}</span></div>
              </div>
              <div className="nlor-detail-section">
                <h4>Personnel & Département</h4>
                <div className="nlor-field"><span className="nlor-field-label">Concession</span><span className="nlor-field-value">{or.dealership_name}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Département</span><span className="nlor-field-value">{or.department_id} {or.dept_section_id && `(${or.dept_section_id})`}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Réceptionnaire entrée</span><span className="nlor-field-value">{or.receptionist_in || '—'}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Réceptionnaire sortie</span><span className="nlor-field-value">{or.receptionist_out || '—'}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Mécanicien</span><span className="nlor-field-value">{or.mechanic_id || '—'}</span></div>
              </div>
              <div className="nlor-detail-section">
                <h4>Flags</h4>
                <div className="nlor-field"><span className="nlor-field-label">Terminé</span><span className="nlor-field-value">{or.is_finished ? '✅ Oui' : '❌ Non'}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Véhicule restitué</span><span className="nlor-field-value">{or.is_veh_handed_back ? '✅ Oui' : '❌ Non'}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Client attend</span><span className="nlor-field-value">{or.is_customer_waiting ? '⏳ Oui' : 'Non'}</span></div>
              </div>
              <div className="nlor-detail-section">
                <h4>Montants</h4>
                <div className="nlor-field"><span className="nlor-field-label">Total HT</span><span className="nlor-field-value">{formatMoney(or.total_ht)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">TVA</span><span className="nlor-field-value">{formatMoney(or.total_tva)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Total TTC</span><span className="nlor-field-value" style={{ fontWeight: 700, fontSize: '1rem' }}>{formatMoney(or.total_ttc)}</span></div>
                <div className="nlor-field"><span className="nlor-field-label">Documents</span><span className="nlor-field-value">{or.nb_documents}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NextlaneOR;
